<template>
  <div class="modal-overlay" @click="$emit('close')">
    <div class="modal-content" @click.stop>
      <div class="modal-header">
        <h2 class="modal-title">
          <i :class="type === 'sortie' ? 'fas fa-arrow-right' : 'fas fa-arrow-left'"></i>
          {{ type === 'sortie' ? 'Nouveau Bon de Sortie' : 'Nouveau Bon de Retour' }}
        </h2>
        <button @click="$emit('close')" class="close-btn">
          <i class="fas fa-times"></i>
        </button>
      </div>
      
      <form @submit.prevent="handleSubmit" class="modal-body">
        <div class="form-sections">
          <!-- Informations générales -->
          <div class="form-section">
            <h3 class="section-title">Informations Générales</h3>
            <div class="form-grid">
              <div class="form-group">
                <label for="technicien" class="required">Technicien</label>
                <select
                  id="technicien"
                  v-model="form.technicien"
                  :class="{ error: errors.technicien }"
                  required
                >
                  <option value="">Sélectionner un technicien</option>
                  <option v-for="tech in technicians" :key="tech._id" :value="tech._id">
                    {{ tech.prenom }} {{ tech.nom }}
                  </option>
                </select>
                <span v-if="errors.technicien" class="error-message">{{ errors.technicien }}</span>
              </div>
              
              <div class="form-group" v-if="type === 'retour'">
                <label for="bonSortieOriginal" class="required">Bon de Sortie Original</label>
                <select
                  id="bonSortieOriginal"
                  v-model="form.bonSortieOriginal"
                  :class="{ error: errors.bonSortieOriginal }"
                  required
                  @change="onBonSortieChange"
                >
                  <option value="">Sélectionner un bon de sortie</option>
                  <option v-for="bon in bonsSortie" :key="bon._id" :value="bon._id">
                    {{ bon.numeroBon }} - {{ formatDate(bon.dateHeureSortie) }}
                  </option>
                </select>
                <span v-if="errors.bonSortieOriginal" class="error-message">{{ errors.bonSortieOriginal }}</span>
              </div>
              
              <div class="form-group">
                <label for="motif" class="required">Motif</label>
                <textarea
                  id="motif"
                  v-model="form.motif"
                  :class="{ error: errors.motif }"
                  :placeholder="type === 'sortie' ? 'Motif de la sortie' : 'Motif du retour'"
                  rows="3"
                  required
                ></textarea>
                <span v-if="errors.motif" class="error-message">{{ errors.motif }}</span>
              </div>
              
              <div class="form-group" v-if="type === 'sortie'">
                <label for="dateRetourPrevue">Date de Retour Prévue</label>
                <input
                  type="date"
                  id="dateRetourPrevue"
                  v-model="form.dateRetourPrevue"
                  :min="today"
                />
              </div>
            </div>
          </div>

          <!-- Articles -->
          <div class="form-section">
            <h3 class="section-title">
              {{ type === 'sortie' ? 'Articles à Sortir' : 'Articles Retournés' }}
              <button type="button" @click="addArticle" class="btn-add-article">
                <i class="fas fa-plus"></i>
                Ajouter
              </button>
            </h3>
            
            <div class="articles-list">
              <div v-for="(article, index) in form.articles" :key="index" class="article-item">
                <div class="article-header">
                  <span class="article-number">#{{ index + 1 }}</span>
                  <button type="button" @click="removeArticle(index)" class="btn-remove">
                    <i class="fas fa-trash"></i>
                  </button>
                </div>
                
                <div class="article-form">
                  <div class="form-group">
                    <label class="required">Produit</label>
                    <select
                      v-model="article.produit"
                      :class="{ error: errors[`articles.${index}.produit`] }"
                      required
                      @change="onProductSelect(index)"
                    >
                      <option value="">Sélectionner un produit</option>
                      <option v-for="product in availableProducts" :key="product._id" :value="product._id">
                        {{ product.referenceInterne }} - {{ product.designation }}
                      </option>
                    </select>
                  </div>
                  
                  <div class="form-group">
                    <label class="required">Quantité</label>
                    <input
                      type="number"
                      v-model.number="article.quantite"
                      :class="{ error: errors[`articles.${index}.quantite`] }"
                      min="1"
                      required
                    />
                  </div>
                  
                  <div class="form-group" v-if="type === 'retour'">
                    <label>Quantité Consommée</label>
                    <input
                      type="number"
                      v-model.number="article.quantiteConsommee"
                      min="0"
                      :max="article.quantite"
                    />
                  </div>
                  
                  <div class="form-group" v-if="needsSerialNumber(article.produit)">
                    <label>{{ getSerialPlaceholder(article.produit) }}</label>
                    <input
                      v-if="getSerialPlaceholder(article.produit) === 'IMEI'"
                      type="text"
                      v-model="article.imei"
                      placeholder="IMEI"
                    />
                    <input
                      v-else
                      type="text"
                      v-model="article.iccid"
                      placeholder="ICCID"
                    />
                  </div>
                  
                  <div class="form-group" v-if="type === 'retour'">
                    <label class="required">État</label>
                    <select v-model="article.etatRetour" required>
                      <option value="bon_etat">Bon état</option>
                      <option value="defectueux">Défectueux</option>
                      <option value="endommage">Endommagé</option>
                      <option value="perdu">Perdu</option>
                    </select>
                  </div>
                  
                  <div class="form-group" v-if="type === 'retour' && article.etatRetour === 'defectueux'">
                    <label class="required">Motif Défectueux</label>
                    <textarea
                      v-model="article.motifDefectueux"
                      placeholder="Décrire le problème"
                      rows="2"
                      required
                    ></textarea>
                  </div>
                  
                  <div class="form-group">
                    <label>Observations</label>
                    <textarea
                      v-model="article.observations"
                      placeholder="Observations particulières"
                      rows="2"
                    ></textarea>
                  </div>
                </div>
              </div>
              
              <div v-if="form.articles.length === 0" class="no-articles">
                <i class="fas fa-box-open"></i>
                <p>Aucun article ajouté</p>
                <button type="button" @click="addArticle" class="btn btn-primary">
                  <i class="fas fa-plus"></i>
                  Ajouter le premier article
                </button>
              </div>
            </div>
          </div>

          <!-- Informations complémentaires -->
          <div class="form-section" v-if="type === 'sortie'">
            <h3 class="section-title">Informations Complémentaires</h3>
            <div class="form-grid">
              <div class="form-group">
                <label for="interventionLiee">Intervention Liée</label>
                <input
                  type="text"
                  id="interventionLiee"
                  v-model="form.interventionLiee"
                  placeholder="Numéro d'intervention"
                />
              </div>
              
              <div class="form-group">
                <label for="clientConcerne">Client Concerné</label>
                <input
                  type="text"
                  id="clientConcerne"
                  v-model="form.clientConcerne"
                  placeholder="Nom du client"
                />
              </div>
              
              <div class="form-group">
                <label for="observations">Observations</label>
                <textarea
                  id="observations"
                  v-model="form.observations"
                  placeholder="Observations générales"
                  rows="3"
                ></textarea>
              </div>
            </div>
          </div>
        </div>
      </form>
      
      <div class="modal-footer">
        <div class="footer-info" v-if="form.articles.length > 0">
          <span class="articles-count">{{ form.articles.length }} article(s)</span>
          <span class="total-value">Valeur estimée: {{ formatPrice(totalValue) }}</span>
        </div>
        <div class="footer-actions">
          <button type="button" @click="$emit('close')" class="btn btn-secondary">
            Annuler
          </button>
          <button type="submit" @click="handleSubmit" class="btn btn-primary" :disabled="loading || form.articles.length === 0">
            <i v-if="loading" class="fas fa-spinner fa-spin"></i>
            <i v-else :class="type === 'sortie' ? 'fas fa-arrow-right' : 'fas fa-arrow-left'"></i>
            {{ type === 'sortie' ? 'Créer Bon de Sortie' : 'Créer Bon de Retour' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, reactive, computed } from 'vue'

export default {
  name: 'BonModal',
  props: {
    bon: {
      type: Object,
      default: null
    },
    type: {
      type: String,
      required: true,
      validator: value => ['sortie', 'retour'].includes(value)
    }
  },
  emits: ['close', 'save'],
  setup(props, { emit }) {
    const loading = ref(false)
    const errors = reactive({})
    
    const form = reactive({
      technicien: '',
      bonSortieOriginal: '',
      motif: '',
      dateRetourPrevue: '',
      interventionLiee: '',
      clientConcerne: '',
      observations: '',
      articles: []
    })

    // Mock data - à remplacer par des appels API
    const technicians = ref([])
    const availableProducts = ref([])
    const bonsSortie = ref([])

    // Date d'aujourd'hui pour la validation
    const today = computed(() => {
      return new Date().toISOString().split('T')[0]
    })

    // Valeur totale estimée
    const totalValue = computed(() => {
      return form.articles.reduce((total, article) => {
        const product = availableProducts.value.find(p => p._id === article.produit)
        return total + (product ? product.prixVente * article.quantite : 0)
      }, 0)
    })

    // Méthodes
    const addArticle = () => {
      const newArticle = {
        produit: '',
        quantite: 1,
        quantiteConsommee: 0,
        numeroSerie: '',
        imei: '',
        iccid: '',
        observations: '',
        etatRetour: 'bon_etat',
        motifDefectueux: ''
      }
      form.articles.push(newArticle)
    }

    const removeArticle = (index) => {
      form.articles.splice(index, 1)
    }

    const onProductSelect = (index) => {
      const article = form.articles[index]
      const product = availableProducts.value.find(p => p._id === article.produit)
      
      if (product) {
        // Pré-remplir les informations du produit
        if (product.imei) article.imei = product.imei
        if (product.iccid) article.iccid = product.iccid
        if (product.numeroSerie) article.numeroSerie = product.numeroSerie
      }
    }

    const onBonSortieChange = () => {
      // Charger les articles du bon de sortie original
      const bonOriginal = bonsSortie.value.find(b => b._id === form.bonSortieOriginal)
      if (bonOriginal) {
        form.articles = bonOriginal.articles.map(article => ({
          ...article,
          quantiteRetournee: article.quantite,
          quantiteConsommee: 0,
          etatRetour: 'bon_etat',
          motifDefectueux: ''
        }))
      }
    }

    const needsSerialNumber = (productId) => {
      const product = availableProducts.value.find(p => p._id === productId)
      return product && ['GPS', 'SIM'].includes(product.categorie)
    }

    const getSerialPlaceholder = (productId) => {
      const product = availableProducts.value.find(p => p._id === productId)
      if (!product) return ''
      return product.categorie === 'GPS' ? 'IMEI' : 'ICCID'
    }

    const validateForm = () => {
      Object.keys(errors).forEach(key => delete errors[key])
      
      if (!form.technicien) {
        errors.technicien = 'Technicien requis'
      }
      
      if (props.type === 'retour' && !form.bonSortieOriginal) {
        errors.bonSortieOriginal = 'Bon de sortie original requis'
      }
      
      if (!form.motif.trim()) {
        errors.motif = 'Motif requis'
      }
      
      if (form.articles.length === 0) {
        errors.articles = 'Au moins un article requis'
      }
      
      // Validation des articles
      form.articles.forEach((article, index) => {
        if (!article.produit) {
          errors[`articles.${index}.produit`] = 'Produit requis'
        }
        if (!article.quantite || article.quantite <= 0) {
          errors[`articles.${index}.quantite`] = 'Quantité requise'
        }
        if (props.type === 'retour' && article.etatRetour === 'defectueux' && !article.motifDefectueux) {
          errors[`articles.${index}.motifDefectueux`] = 'Motif requis pour article défectueux'
        }
      })
      
      return Object.keys(errors).length === 0
    }

    const handleSubmit = async () => {
      if (!validateForm()) {
        return
      }

      loading.value = true
      try {
        const bonData = { ...form }
        emit('save', bonData)
      } catch (error) {
        console.error('Erreur lors de la sauvegarde:', error)
      } finally {
        loading.value = false
      }
    }

    const formatDate = (date) => {
      return new Intl.DateTimeFormat('fr-FR', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      }).format(new Date(date))
    }

    const formatPrice = (price) => {
      return new Intl.NumberFormat('fr-FR', {
        style: 'currency',
        currency: 'TND'
      }).format(price || 0)
    }

    return {
      loading,
      errors,
      form,
      technicians,
      availableProducts,
      bonsSortie,
      today,
      totalValue,
      addArticle,
      removeArticle,
      onProductSelect,
      onBonSortieChange,
      needsSerialNumber,
      getSerialPlaceholder,
      handleSubmit,
      formatDate,
      formatPrice
    }
  }
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
}

.modal-content {
  background: white;
  border-radius: 12px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  max-width: 900px;
  width: 100%;
  max-height: 90vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 1px solid #e9ecef;
  background: linear-gradient(135deg, #007bff 0%, #0056b3 100%);
  color: white;
}

.modal-title {
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 1.25rem;
  font-weight: 600;
}

.close-btn {
  background: none;
  border: none;
  color: white;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 6px;
  transition: background-color 0.3s ease;
}

.close-btn:hover {
  background: rgba(255, 255, 255, 0.1);
}

.modal-body {
  flex: 1;
  overflow-y: auto;
  padding: 1.5rem;
}

.form-sections {
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.form-section {
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 1.5rem;
  background: #f8f9fa;
}

.section-title {
  margin: 0 0 1rem 0;
  font-size: 1.1rem;
  font-weight: 600;
  color: #495057;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid #dee2e6;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.btn-add-article {
  background: #28a745;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9rem;
  transition: all 0.3s ease;
}

.btn-add-article:hover {
  background: #218838;
  transform: translateY(-2px);
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1rem;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.form-group label {
  font-weight: 600;
  color: #495057;
  font-size: 0.9rem;
}

.form-group label.required::after {
  content: ' *';
  color: #dc3545;
}

.form-group input,
.form-group select,
.form-group textarea {
  padding: 0.75rem;
  border: 2px solid #e9ecef;
  border-radius: 6px;
  font-size: 0.9rem;
  transition: all 0.3s ease;
  background: white;
}

.form-group input:focus,
.form-group select:focus,
.form-group textarea:focus {
  outline: none;
  border-color: #007bff;
  box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
}

.form-group input.error,
.form-group select.error,
.form-group textarea.error {
  border-color: #dc3545;
  box-shadow: 0 0 0 3px rgba(220, 53, 69, 0.1);
}

.error-message {
  color: #dc3545;
  font-size: 0.8rem;
  margin-top: 0.25rem;
}

.articles-list {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.article-item {
  background: white;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  padding: 1rem;
}

.article-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #e9ecef;
}

.article-number {
  font-weight: 600;
  color: #007bff;
}

.btn-remove {
  background: #dc3545;
  color: white;
  border: none;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.8rem;
  transition: all 0.3s ease;
}

.btn-remove:hover {
  background: #c82333;
}

.article-form {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
}

.no-articles {
  text-align: center;
  padding: 3rem;
  color: #6c757d;
}

.no-articles i {
  font-size: 3rem;
  margin-bottom: 1rem;
  opacity: 0.5;
}

.modal-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-top: 1px solid #e9ecef;
  background: #f8f9fa;
  flex-wrap: wrap;
  gap: 1rem;
}

.footer-info {
  display: flex;
  gap: 2rem;
  color: #6c757d;
  font-size: 0.9rem;
}

.articles-count {
  font-weight: 600;
}

.total-value {
  color: #28a745;
  font-weight: 600;
}

.footer-actions {
  display: flex;
  gap: 0.75rem;
}

.btn {
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.3s ease;
}

.btn-primary {
  background: #007bff;
  color: white;
}

.btn-secondary {
  background: #6c757d;
  color: white;
}

.btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

@media (max-width: 768px) {
  .modal-overlay {
    padding: 0.5rem;
  }
  
  .form-grid,
  .article-form {
    grid-template-columns: 1fr;
  }
  
  .modal-footer {
    flex-direction: column;
    align-items: stretch;
  }
  
  .footer-actions {
    justify-content: stretch;
  }
  
  .btn {
    flex: 1;
    justify-content: center;
  }
}
</style>
